import { useCallback, useEffect, useRef, useState } from "react";
import { api, stopPtzOnUnload, type PTZVector } from "../../lib/api";

/** How often a held direction re-sends its move command.
 *
 *  This is the client half of the safety contract described in the backend's
 *  usecase.PTZConfig.MoveTTL: the server arms a watchdog on every move and
 *  stops the camera by itself if the next one does not arrive in time. This
 *  interval must stay comfortably below that TTL (2500 ms) or an operator
 *  holding a button would see the camera stutter to a halt and restart.
 *
 *  It is also what makes a dropped packet a non-event: the next keep-alive
 *  re-arms the move a second later, rather than the gesture dying silently. */
const KEEPALIVE_MS = 1000;

/** Speed for a held direction press, as a fraction of the device's maximum.
 *
 *  Deliberately not 1.0. A dome at full speed sweeps past whatever the
 *  operator was trying to look at before they can release the button, which
 *  makes fine framing impossible and is the single most common complaint
 *  about naive PTZ controls. Slow enough to aim, fast enough not to feel
 *  broken. */
const MOVE_SPEED = 0.6;
const ZOOM_SPEED = 0.5;

export interface PtzControl {
  /** Begin a held move. Safe to call repeatedly; the newest vector wins. */
  startMove: (vector: PTZVector) => void;
  /** End a held move. Idempotent — several teardown paths call it for one
   *  gesture, and calling it when nothing is moving is not an error. */
  endMove: () => void;
  gotoPreset: (preset: number) => void;
  /** True while a move is being held. Drives the pressed state on the pad. */
  isMoving: boolean;
  /** The last command failure, or null. Cleared when a later command
   *  succeeds, so a transient blip doesn't leave a stale warning on screen. */
  error: string | null;
}

/** Builds a vector for one of the eight compass directions plus zoom.
 *
 *  Exported for the pad's button table and for tests: the mapping from "the
 *  operator pressed up-left" to a signed vector is the sort of thing that is
 *  easy to get subtly mirrored, and much easier to assert on directly than
 *  through a rendered component. */
export function directionVector(pan: number, tilt: number): PTZVector {
  return { pan: pan * MOVE_SPEED, tilt: tilt * MOVE_SPEED, zoom: 0 };
}

export function zoomVector(direction: 1 | -1): PTZVector {
  return { pan: 0, tilt: 0, zoom: direction * ZOOM_SPEED };
}

/** Drives one camera's mount, owning the keep-alive and every teardown path.
 *
 *  The teardown paths are the substance of this hook. A move that is started
 *  and never stopped leaves a camera physically turning, so every way a
 *  gesture can end without a clean pointerup is handled here rather than left
 *  to each caller to remember: unmount, the tab being hidden, the window
 *  losing focus, and the page unloading. */
export function usePtzControl(cameraId: string, isEnabled: boolean): PtzControl {
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs, not state: these are read inside timers and event listeners that
  // must see the current value without re-subscribing on every render.
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const vectorRef = useRef<PTZVector | null>(null);
  // Guards the stop request: without it, every pointerup — including the ones
  // from a pointercancel that already stopped — would fire another request.
  const isMovingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const sendMove = useCallback(
    (vector: PTZVector) => {
      api.ptz
        .move(cameraId, vector)
        .then(() => setError(null))
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "Couldn't move the camera.");
        });
    },
    [cameraId],
  );

  // False from the moment teardown begins, so a request that outlives this
  // hook cannot report into a component that is no longer rendering.
  const isMountedRef = useRef(true);

  const endMove = useCallback(() => {
    clearTimer();
    vectorRef.current = null;
    if (!isMovingRef.current) return;
    isMovingRef.current = false;
    setIsMoving(false);

    api.ptz.stop(cameraId).catch((err: unknown) => {
      // Worth surfacing rather than swallowing: the camera is still moving
      // until the backend's watchdog catches it, and the operator should
      // know their release didn't land.
      //
      // Unless this hook is already gone. The unmount path below calls
      // endMove() precisely so a closing dialog still stops the camera, and
      // that request must keep flying — but there is no longer any surface to
      // report its failure on, and reporting it anyway was leaking a rejection
      // into whatever ran next. It reddened the suite roughly one run in five,
      // as an error attributed to a test that had already passed.
      if (!isMountedRef.current) return;
      setError(err instanceof Error ? err.message : "Couldn't stop the camera.");
    });
  }, [cameraId, clearTimer]);

  const startMove = useCallback(
    (vector: PTZVector) => {
      if (!isEnabled) return;
      vectorRef.current = vector;
      isMovingRef.current = true;
      setIsMoving(true);
      sendMove(vector);

      // Restart the interval on every press so a direction change re-bases
      // the keep-alive rather than inheriting the previous button's phase,
      // which could otherwise fire the next one immediately.
      clearTimer();
      timerRef.current = setInterval(() => {
        const current = vectorRef.current;
        if (current) sendMove(current);
      }, KEEPALIVE_MS);
    },
    [isEnabled, sendMove, clearTimer],
  );

  const gotoPreset = useCallback(
    (preset: number) => {
      if (!isEnabled) return;
      // A preset recall supersedes a manual move; leaving the keep-alive
      // running would fight the mount all the way to its destination.
      endMove();
      api.ptz
        .gotoPreset(cameraId, preset)
        .then(() => setError(null))
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "Couldn't recall that preset.");
        });
    },
    [cameraId, isEnabled, endMove],
  );

  // Every way a gesture can end other than a clean pointerup.
  useEffect(() => {
    // Re-armed here, not only initialised at declaration: this effect's
    // cleanup also runs whenever its deps change, and that path ends with the
    // hook still mounted. Without this the flag would latch false on the
    // first cameraId change and silently suppress every later error.
    isMountedRef.current = true;

    const stopIfHidden = () => {
      if (document.visibilityState === "hidden") endMove();
    };
    const onUnload = () => {
      if (isMovingRef.current) stopPtzOnUnload(cameraId);
    };

    // blur covers alt-tabbing away mid-press: the pointerup lands on the OS,
    // not on this document, so without this the button stays "held" forever.
    window.addEventListener("blur", endMove);
    document.addEventListener("visibilitychange", stopIfHidden);
    // pagehide rather than unload: unload is ignored by browsers that keep
    // the page in the back/forward cache, and is deprecated on mobile Safari.
    window.addEventListener("pagehide", onUnload);

    return () => {
      window.removeEventListener("blur", endMove);
      document.removeEventListener("visibilitychange", stopIfHidden);
      window.removeEventListener("pagehide", onUnload);
      // Unmount is itself a teardown path — closing the focus dialog mid-press
      // must stop the camera, not leave it turning behind a closed overlay.
      // Marked unmounted first: the stop still goes out, only its error has
      // nowhere left to land.
      isMountedRef.current = false;
      endMove();
    };
  }, [cameraId, endMove]);

  return { startMove, endMove, gotoPreset, isMoving, error };
}
